/**
 * Message Bubble Component
 * Displays individual message with sender info and attachments - WhatsApp style
 */

import React, { useState, useRef, useEffect } from 'react';
import { Message } from '@/types/messaging';
import { Image as ImageIcon, File, Mic, Video, ExternalLink, Check, CheckCheck, Smile, Forward, ClipboardList } from 'lucide-react';
import { formatFileSize } from '@/utils/messagingAttachments';
import { messagingService } from '@/firebase/messagingService';
import EmojiPicker from './EmojiPicker';
import { useAuth } from '@/contexts/AuthContext';
import TaskCard from './TaskCard';
import ContractCard from './ContractCard';
import VendorCard from './VendorCard';
import ChecklistCard from './ChecklistCard';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { TaskWithDetails } from '@/services/taskService';
import { AgreementDocument } from '@/firebase/agreementService';
import { Vendor } from '@/firebase/vendorService';
import { ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { userService } from '@/firebase/database';
import { Mention } from '@/types/messaging';

// Helper function to get translated field (same pattern as taskService)
// When language is null/undefined, defaults to original (message.text) to avoid showing
// wrong translation when preference hasn't loaded or for Spanish locale variants (es-MX, etc.)
const getTranslatedField = (
  englishValue: string | undefined,
  spanishValue: string | undefined,
  language: string | undefined | null,
  originalText?: string
): string => {
  if (!englishValue && !spanishValue && !originalText) return '';
  // When preference unknown: show original to avoid "changed" text when both users have Spanish
  const lang = language?.toLowerCase?.() ?? '';
  if (!lang || lang === 'unknown') {
    return (originalText && originalText.trim()) || (spanishValue && spanishValue.trim()) || englishValue || '';
  }
  if (lang === 'es' || lang.startsWith('es-') || lang.startsWith('es_')) {
    return (spanishValue && spanishValue.trim().length > 0) ? spanishValue : (englishValue || '');
  }
  return englishValue || '';
};

/**
 * Component to render text with highlighted mentions
 */
const MentionText: React.FC<{ text: string; mentions?: Mention[]; className?: string }> = ({ 
  text, 
  mentions = [], 
  className = '' 
}) => {
  if (!mentions || mentions.length === 0) {
    return <p className={className}>{text}</p>;
  }

  // Sort mentions by startIndex
  const sortedMentions = [...mentions].sort((a, b) => a.startIndex - b.startIndex);
  
  // Build array of text parts and mention spans
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  sortedMentions.forEach((mention) => {
    // Add text before mention
    if (mention.startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, mention.startIndex));
    }
    
    // Add highlighted mention
    parts.push(
      <span
        key={`mention-${mention.userId}-${mention.startIndex}`}
        className="bg-blue-100 text-blue-700 font-medium px-1 rounded"
        title={`Mentioned: ${mention.userName}`}
      >
        {text.substring(mention.startIndex, mention.endIndex)}
      </span>
    );
    
    lastIndex = mention.endIndex;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <p className={className}>{parts}</p>;
};

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  onForward?: (message: Message) => void;
  onCreateTask?: (message: Message) => void;
  onViewTask?: (task: TaskWithDetails) => void;
  onViewContract?: (contract: AgreementDocument) => void;
  onViewVendor?: (vendor: Vendor) => void;
  onViewChecklist?: (completion: ChecklistCompletion) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn, onForward, onCreateTask, onViewTask, onViewContract, onViewVendor, onViewChecklist }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { hasFeature } = useSubscription();
  const [imageError, setImageError] = useState<Set<string>>(new Set());
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [userPreferredLanguage, setUserPreferredLanguage] = useState<string | undefined>(undefined);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Get user's preferred language
  useEffect(() => {
    const fetchUserLanguage = async () => {
      if (user?.id) {
        try {
          const userResult = await userService.getUser(user.id);
          if (userResult.success && userResult.data) {
            const preferredLang = userResult.data.preferredLanguage as string | undefined;
            const lang = preferredLang === 'es' ? 'es' : 'en';
            setUserPreferredLanguage(lang);
          }
        } catch (error) {
          console.error('Error fetching user preferred language:', error);
        }
      }
    };
    fetchUserLanguage();
  }, [user?.id]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        bubbleRef.current &&
        !bubbleRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmojiPicker]);

  const formatTime = (timestamp: any): string => {
    if (!timestamp) return '';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / 86400000);

      const timeString = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      // Today - just show time
      if (diffDays === 0) {
        return timeString;
      }
      
      // Yesterday
      if (diffDays === 1) {
        return `Yesterday ${timeString}`;
      }
      
      // This week
      if (diffDays < 7) {
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
        return `${weekday} ${timeString}`;
      }

      // Older - show date with time
      const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${dateString} ${timeString}`;
    } catch (error) {
      return '';
    }
  };

  const handleImageError = (url: string) => {
    setImageError((prev) => new Set(prev).add(url));
  };

  const handleReaction = async (emoji: string) => {
    if (!message.id || !user?.id || reacting) return;

    setReacting(true);
    try {
      await messagingService.addReaction(message.id, emoji, user.id);
    } catch (error) {
      console.error('Error adding reaction:', error);
    } finally {
      setReacting(false);
      setShowEmojiPicker(false);
    }
  };

  const handleReactionClick = (reaction: { emoji: string; userId: string }) => {
    // If user's own reaction, remove it by clicking again
    if (reaction.userId === user?.id) {
      if (message.id && user?.id) {
        messagingService.removeReaction(message.id, user.id).catch((error) => {
          console.error('Error removing reaction:', error);
        });
      }
    } else {
      // Toggle reaction by adding same emoji
      handleReaction(reaction.emoji);
    }
  };

  const handleForwardClick = () => {
    if (onForward) {
      onForward(message);
    }
  };

  // Get reactions grouped by emoji
  const groupedReactions = React.useMemo(() => {
    if (!message.reactions || message.reactions.length === 0) return [];
    
    const groups = new Map<string, typeof message.reactions>();
    message.reactions.forEach((reaction) => {
      const existing = groups.get(reaction.emoji) || [];
      existing.push(reaction);
      groups.set(reaction.emoji, existing);
    });
    
    return Array.from(groups.entries()).map(([emoji, reactions]) => ({
      emoji,
      reactions: reactions || [],
      count: (reactions || []).length,
    }));
  }, [message.reactions]);

  const hasAttachments = message.attachments && message.attachments.length > 0;
  
  // Get display text (translated if user prefers Spanish)
  // For voice messages, use transcription; otherwise use text
  let displayText: string | null = null;
  if (message.transcription?.originalText) {
    // Voice message - use transcription with bidirectional translation
    const transcription = message.transcription;
    const sourceLanguage = transcription.sourceLanguage?.toLowerCase() || 'en';
    const isSourceSpanish = sourceLanguage.startsWith('es');
    
    // Determine which text is English and which is Spanish
    let englishText: string | undefined;
    let spanishText: string | undefined;
    
    // Backward compatibility: If targetLanguage is missing/null, assume old format
    if (!transcription.targetLanguage && transcription.translatedText) {
      // Old format: originalText is English, translatedText is Spanish
      englishText = transcription.originalText;
      spanishText = transcription.translatedText;
    } else if (isSourceSpanish) {
      // New format: Source is Spanish → originalText is Spanish, translatedText is English
      spanishText = transcription.originalText;
      englishText = transcription.translatedText;
    } else {
      // New format: Source is English → originalText is English, translatedText is Spanish
      englishText = transcription.originalText;
      spanishText = transcription.translatedText;
    }
    
    displayText = getTranslatedField(englishText, spanishText, userPreferredLanguage, message.transcription?.originalText) || null;
  } else if (message.text && message.text !== '📎') {
    // Regular text message - use text with bidirectional translation
    // Check if text_en exists (meaning original text was Spanish)
    let englishText: string | undefined;
    let spanishText: string | undefined;
    
    if (message.text_en && message.text_en.trim().length > 0) {
      // Original text is Spanish: text is Spanish, text_en is English translation
      spanishText = message.text;
      englishText = message.text_en;
    } else {
      // Original text is English: text is English, text_es is Spanish translation
      englishText = message.text;
      spanishText = message.text_es;
    }
    
    // Pass message.text (always the original) so when preference is unknown we show original
    displayText = getTranslatedField(englishText, spanishText, userPreferredLanguage, message.text);
  }

  return (
    <div 
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-1 group`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`max-w-[65%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col relative`}>
        {!isOwn && (
          <p className="text-xs text-gray-500 mb-0.5 px-1 font-medium">{message.sentByName}</p>
        )}
        
        {/* Action buttons - shown on hover */}
        {isHovered && (
          <div className={`absolute ${isOwn ? 'right-full mr-2' : 'left-full ml-2'} top-1/2 transform -translate-y-1/2 flex gap-1 z-10`}>
            <div className="relative" ref={emojiPickerRef}>
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                title="React"
              >
                <Smile className="w-4 h-4 text-gray-600" />
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onSelect={handleReaction}
                  onClose={() => setShowEmojiPicker(false)}
                />
              )}
            </div>
            {onCreateTask && !message.linkedItems?.taskId && hasFeature('create_custom_tasks') && (
              <button
                onClick={() => onCreateTask(message)}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                title="Create Task"
              >
                <ClipboardList className="w-4 h-4 text-gray-600" />
              </button>
            )}
            {onForward && (
              <button
                onClick={handleForwardClick}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                title="Forward"
              >
                <Forward className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        )}
        
        <div
          ref={bubbleRef}
          className={`px-2 py-1.5 rounded-lg shadow-sm ${
            isOwn
              ? 'bg-gray-200'
              : 'bg-white'
          }`}
          style={{
            borderRadius: isOwn ? '7.5px 7.5px 0 7.5px' : '0 7.5px 7.5px 7.5px',
          }}
        >
          {displayText && message.mentions && message.mentions.length > 0 ? (
            <MentionText 
              text={displayText} 
              mentions={message.mentions} 
              className={`text-sm whitespace-pre-wrap break-words ${
                isOwn ? 'text-gray-900' : 'text-gray-900'
              }`}
            />
          ) : displayText ? (
            <p className={`text-sm whitespace-pre-wrap break-words ${
              isOwn ? 'text-gray-900' : 'text-gray-900'
            }`}>
              {displayText}
            </p>
          ) : null}

          {/* Attachments */}
          {hasAttachments && (
            <div className="mt-1 space-y-2">
              {message.attachments!.map((attachment, index) => (
                <div key={index} className="rounded-lg overflow-hidden">
                  {attachment.type === 'image' && (
                    <div className="relative">
                      {!imageError.has(attachment.url) ? (
                        <img
                          src={attachment.url}
                          alt={attachment.name}
                          className="max-w-full max-h-64 rounded-lg cursor-pointer"
                          onError={() => handleImageError(attachment.url)}
                          onClick={() => window.open(attachment.url, '_blank')}
                        />
                      ) : (
                        <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                          <span className="ml-2 text-sm text-gray-500">Failed to load image</span>
                        </div>
                      )}
                    </div>
                  )}

                  {attachment.type === 'video' && (
                    <div className="relative rounded-lg overflow-hidden">
                      <video
                        src={attachment.url}
                        controls
                        className="max-w-full max-h-64 rounded-lg"
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}

                  {attachment.type === 'audio' && (
                    <div className="bg-white/50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <Mic className={`w-5 h-5 ${isOwn ? 'text-gray-700' : 'text-gray-600'}`} />
                        <audio
                          src={attachment.url}
                          controls
                          className="flex-1"
                          style={{ maxWidth: '300px' }}
                        />
                        {attachment.name && (
                          <span className="text-xs text-gray-500 truncate max-w-[100px]">
                            {attachment.name}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {attachment.type === 'file' && (
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
                        isOwn ? 'bg-white/30' : 'bg-gray-100'
                      }`}
                    >
                      <File className={`w-5 h-5 ${isOwn ? 'text-gray-700' : 'text-gray-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${isOwn ? 'text-gray-900' : 'text-gray-900'}`}>
                          {attachment.name}
                        </p>
                        {attachment.size && (
                          <p className={`text-xs ${isOwn ? 'text-gray-600' : 'text-gray-500'}`}>
                            {formatFileSize(attachment.size)}
                          </p>
                        )}
                      </div>
                      <ExternalLink className={`w-4 h-4 ${isOwn ? 'text-gray-700' : 'text-gray-500'}`} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Linked Task Card */}
          {message.linkedItems?.taskId && currentOrganization?.id && (
            <div className="mt-2">
              <TaskCard
                taskId={message.linkedItems.taskId}
                organizationId={currentOrganization.id}
                onViewTask={onViewTask}
              />
            </div>
          )}

          {/* Linked Contract Card */}
          {message.linkedItems?.contractId && currentOrganization?.id && (
            <div className="mt-2">
              <ContractCard
                contractId={message.linkedItems.contractId}
                organizationId={currentOrganization.id}
                onViewContract={onViewContract}
              />
            </div>
          )}

          {/* Linked Vendor Card */}
          {message.linkedItems?.vendorId && currentOrganization?.id && (
            <div className="mt-2">
              <VendorCard
                vendorId={message.linkedItems.vendorId}
                organizationId={currentOrganization.id}
                onViewVendor={onViewVendor}
              />
            </div>
          )}

          {/* Linked Checklist Completion Card */}
          {message.linkedItems?.checklistCompletionId && currentOrganization?.id && (
            <div className="mt-2">
              <ChecklistCard
                completionId={message.linkedItems.checklistCompletionId}
                organizationId={currentOrganization.id}
                onViewChecklist={onViewChecklist}
              />
            </div>
          )}

          {/* Timestamp and read receipt */}
          <div className={`flex items-center justify-end space-x-1 mt-0.5 ${displayText || hasAttachments || message.linkedItems?.taskId || message.linkedItems?.contractId || message.linkedItems?.vendorId || message.linkedItems?.checklistCompletionId ? 'pt-0.5' : ''}`}>
            <span className={`text-xs ${isOwn ? 'text-gray-500' : 'text-gray-500'}`}>
              {formatTime(message.createdAt)}
            </span>
            {isOwn && (
              <span className="text-gray-500">
                <CheckCheck className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        </div>
        
        {/* Reactions */}
        {groupedReactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {groupedReactions.map((group, index) => {
              const userReacted = group.reactions.some(r => r.userId === user?.id);
              return (
                <button
                  key={index}
                  onClick={() => handleReactionClick(group.reactions[0])}
                  className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition-colors ${
                    userReacted
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{group.emoji}</span>
                  <span>{group.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
